// ViewingRequest.jsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Pagination,
  Snackbar,
  Alert,
} from "@mui/material";
import useViewingRequests from "../../hooks/useViewingRequests";
import { useAuth } from "../../hooks/useAuth";
import ViewingRequestsStyles from "./ViewingRequests.styles";
import fetchQuestionnaires from "../../helpers/fetchQuestionnaires";
import ViewingRequestCard from "./ViewingRequestCard";
import { FIELD_LABELS } from "../../data/fieldLabels";
import createZoomMeeting from "../../helpers/createZoomMeeting";

// Helper to format field labels
function labelize(key) {
  return FIELD_LABELS[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
}
const EXCLUDE_FIELDS = ["uid", "submittedAt"];

function renderQuestionnaire(questionnaire) {
  if (!questionnaire || Object.values(questionnaire).every((v) => !v || String(v).trim() === "")) {
    return (
      <Typography sx={ViewingRequestsStyles.infoText} color="text.secondary">
        No questionnaire submitted.
      </Typography>
    );
  }
  return (
    <Box sx={{ pl: 1 }}>
      {Object.entries(questionnaire)
        .filter(
          ([key, value]) =>
            !EXCLUDE_FIELDS.includes(key) &&
            value !== "" &&
            value !== undefined &&
            value !== null
        )
        .map(([key, value]) => {
          let displayValue = value;
          // Format values:
          if (key === "moveInDate" && value) {
            displayValue = new Date(value).toLocaleDateString();
          } else if (key === "takeHomeIncome" && value) {
            displayValue = `$${Number(value).toLocaleString()}`;
          } else if ((key === "dog" || key === "cat" || key === "other") && value === "yes") {
            displayValue = "Yes";
          } else if (key === "animals") {
            displayValue = value === "0" ? "None" : value;
          } else if (key === "bankruptcy") {
            displayValue = value === "yes" ? "Yes" : "No";
          }
          return (
            <Typography
              key={key}
              sx={{
                ...ViewingRequestsStyles.infoText,
                fontSize: "0.97rem",
                mb: 0.3,
              }}
            >
              <b style={{ textTransform: "capitalize" }}>
                {labelize(key)}:
              </b>{" "}
              {displayValue}
            </Typography>
          );
        })}
    </Box>
  );
}

// Helper: Get next occurrence of a day (0 = Sunday, ... 6 = Saturday)
function getNextDayOfWeek(dayOfWeek, time) {
  const daysOfWeekMap = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };
  const now = new Date();
  const resultDate = new Date(now);
  resultDate.setHours(0, 0, 0, 0);
  const currentDay = resultDate.getDay();
  let daysToAdd = (daysOfWeekMap[dayOfWeek] - currentDay + 7) % 7;
  if (daysToAdd === 0) daysToAdd = 7; // always move to next week if today

  resultDate.setDate(now.getDate() + daysToAdd);

  // Set the time
  const [timeStr, ampm] = time.split(" ");
  let [hours, minutes] = timeStr.split(":").map(Number);
  if (ampm === "PM" && hours !== 12) hours += 12;
  if (ampm === "AM" && hours === 12) hours = 0;
  resultDate.setHours(hours, minutes);

  return resultDate;
}

const BACKEND_URL =
  (process.env.NODE_ENV === "development"
    ? "http://localhost:4000"
    : "https://ai-realtor-2ktt.onrender.com");

const parseViewingTime = (displayText) => {
  if (displayText.startsWith("Specific Date:")) {
    const match = displayText.match(/Specific Date: ([^,]+), (\d{4}), from ([^ ]+ [AP]M)/);
    if (!match) throw new Error("Invalid specific date format");
    const [, monthDay, year, time] = match;
    const dateStr = `${monthDay}, ${year} ${time}`;
    const parsed = new Date(dateStr);
    return parsed;
  } else if (displayText.startsWith("Weekly (")) {
    const matchWithDate = displayText.match(/Weekly \((\w+)\): ([0-9:]+ [AP]M).*on (\d{4}-\d{2}-\d{2})/);
    if (matchWithDate) {
      const [, , time, dateStr] = matchWithDate;
      const [year, month, day] = dateStr.split("-");
      let [timeStr, ampm] = time.split(" ");
      let [hours, minutes] = timeStr.split(":").map(Number);
      if (ampm === "PM" && hours !== 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;
      const dateObj = new Date(year, Number(month) - 1, day, hours, minutes);
      return dateObj;
    }
    const match = displayText.match(/Weekly \((\w+)\): ([0-9:]+ [AP]M)/);
    if (!match) throw new Error("Invalid weekly format");
    const [, dayOfWeek, time] = match;
    const dateObj = getNextDayOfWeek(dayOfWeek, time);
    return dateObj;
  } else {
    throw new Error("Unknown date format");
  }
};

const ViewingRequests = ({ uid, onGenerateAccessCode }) => {
  const { currentUser } = useAuth();
  const { fetchViewingRequests, patchViewingRequest } = useViewingRequests();
  const [viewingRequests, setViewingRequests] = useState([]);
  const [loadingViewings, setLoadingViewings] = useState(true);
  const [questionnaires, setQuestionnaires] = useState({});
  const [currentTab, setCurrentTab] = useState("approved");
  // const [pendingPage, setPendingPage] = useState(1); // Commented out - pending tab disabled
  const [approvedPage, setApprovedPage] = useState(1);
  const [deniedPage, setDeniedPage] = useState(1);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });
  const requestsPerPage = 5;

  useEffect(() => {
    const loadViewingRequestsAndQuestionnaires = async () => {
      setLoadingViewings(true);
      try {
        const requests = await fetchViewingRequests();
        setViewingRequests(requests);

        // Get all unique listing IDs
        const uniqueListingIds = [
          ...new Set(requests.map((req) => req.listingId)),
        ];
        // Fetch questionnaires for each listingId
        const questionnairesData = {};
        for (const listingId of uniqueListingIds) {
          questionnairesData[listingId] = await fetchQuestionnaires(listingId);
        }
        setQuestionnaires(questionnairesData);
      } catch (error) {
        console.error("Error fetching viewing requests or questionnaires:", error);
      } finally {
        setLoadingViewings(false);
      }
    };
    loadViewingRequestsAndQuestionnaires();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter requests by status
  // Pending requests filter - commented out (pending tab disabled)
  // const pendingRequests = useMemo(
  //   () => viewingRequests.filter((req) => req.status == null),
  //   [viewingRequests]
  // );
  const approvedRequests = useMemo(
    () => {
      return viewingRequests
        .filter((req) => req.status === "approved")
        .sort((a, b) => {
          // Sort by creation time (submittedAt) - newest first
          const timeA = a.submittedAt || 0;
          const timeB = b.submittedAt || 0;
          return timeB - timeA; // descending (newest first)
        });
    },
    [viewingRequests]
  );
  const deniedRequests = useMemo(
    () => viewingRequests
      .filter((req) => req.status === "denied")
      .sort((a, b) => {
        // Sort by creation time (submittedAt) - newest first
        const timeA = a.submittedAt || 0;
        const timeB = b.submittedAt || 0;
        return timeB - timeA; // descending (newest first)
      }),
    [viewingRequests]
  );

  // ----------- Approve, Generate Zoom, and Patch DB ------------
  const handleApproveAndGenerate = async (listingId, requestId, request) => {
    try {
      const displayText = request.dateDetails.displayText;
      const viewingTime = parseViewingTime(displayText);
      if (isNaN(viewingTime.getTime())) {
        throw new Error("Invalid viewing time");
      }

      const agentEmail = currentUser?.email;
      const agentUid = currentUser?.uid;         
      const tenantEmail =
        request?.dateDetails?.email || request.email || "unknown@tenant.com";

      const topic = `Viewing – ${request.address}, ${request.city}`;

      // 1. Create Zoom meeting (existing)
      const zoomResult = await createZoomMeeting({
        dateTime: viewingTime.toISOString(),
        topic,
        agentEmail,
        tenantEmail,
      });
      const { joinUrl, meetingId } = zoomResult;

      // 2. Create Google Calendar event (new — skipped silently if not connected)
      const endTime = new Date(viewingTime.getTime() + 60 * 60 * 1000); // +1 hour
      try {
        await fetch(`${BACKEND_URL}/google-calendar/create-event`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: agentUid,
            summary: topic,
            description: `Tenant: ${tenantEmail}`,
            startISO: viewingTime.toISOString(),
            endISO: endTime.toISOString(),
            attendeeEmail: tenantEmail,
          }),
        });
        // Response is { googleCalendarEventId, htmlLink } if connected,
        // or { skipped: true } if not — either way we continue.
      } catch (gcalErr) {
        // Never block the booking if GCal fails
        console.warn("[GCal] create-event failed (non-fatal):", gcalErr);
      }

      // 3. Patch Firestore (existing)
      await patchViewingRequest(listingId, requestId, {
        status: "approved",
        zoom: {
          joinUrl,
          meetingId,
          startTime: viewingTime.toISOString(),
        },
      });

      // 4. Smart lock access code prep (existing — unchanged)
      const startsAt = new Date(viewingTime.getTime());
      startsAt.setMinutes(startsAt.getMinutes() - 5);
      const endsAt = new Date(viewingTime.getTime());
      endsAt.setMinutes(endsAt.getMinutes() + 15);

      const pad = (n) => n.toString().padStart(2, "0");
      const formattedStartsAt = `${startsAt.getFullYear()}-${pad(startsAt.getMonth() + 1)}-${pad(startsAt.getDate())}T${pad(startsAt.getHours())}:${pad(startsAt.getMinutes())}`;
      const formattedEndsAt = `${endsAt.getFullYear()}-${pad(endsAt.getMonth() + 1)}-${pad(endsAt.getDate())}T${pad(endsAt.getHours())}:${pad(endsAt.getMinutes())}`;
      const fullAddress = `${request.address}, ${request.city}, ${request.state}`;

      const accessCodeData = {
        email: request.email,
        phone: request.phone,
        startsAt: formattedStartsAt,
        endsAt: formattedEndsAt,
        address: fullAddress,
      };
      if (onGenerateAccessCode) onGenerateAccessCode(accessCodeData);

      // 5. Update local state (existing — unchanged)
      setViewingRequests((prev) =>
        prev.map((req) =>
          req.id === requestId
            ? { ...req, status: "approved", zoom: { joinUrl, meetingId, startTime: viewingTime.toISOString() } }
            : req
        )
      );

      setSnack({ open: true, msg: "Viewing approved and Zoom meeting created 🎉", severity: "success" });
    } catch (error) {
      console.error("Error approving and generating access code:", error);
      setSnack({
        open: true,
        msg: error.message || "Failed to approve request or invalid date format.",
        severity: "error",
      });
    }
  };

  const handleDeny = async (listingId, requestId) => {
    try {
      await patchViewingRequest(listingId, requestId, { status: "denied" });
      setViewingRequests((prev) =>
        prev.map((req) =>
          req.id === requestId ? { ...req, status: "denied" } : req
        )
      );
      setSnack({
        open: true,
        msg: "Request denied.",
        severity: "info",
      });
    } catch (error) {
      console.error("Failed to deny request:", error);
      setSnack({
        open: true,
        msg: "Failed to deny request.",
        severity: "error",
      });
    }
  };

  const renderRequests = (requests, page, setPage) => {
    const totalPages = Math.ceil(requests.length / requestsPerPage);
    const startIndex = (page - 1) * requestsPerPage;
    const endIndex = startIndex + requestsPerPage;
    const currentRequests = requests.slice(startIndex, endIndex);

    return (
      <>
        <Box sx={ViewingRequestsStyles.viewingContainer}>
          {currentRequests.map((request) => {
            let qData = null;
            if (questionnaires?.[request.listingId]) {
              const keys = Object.keys(questionnaires[request.listingId]);
              if (keys.length > 0) {
                qData = questionnaires[request.listingId][keys[0]];
              }
            }
            const usedQuestionnaire = request.questionnaire && Object.keys(request.questionnaire).length > 0
              ? request.questionnaire
              : qData;
            return (
              <ViewingRequestCard
                key={request.id}
                request={request}
                questionnaire={usedQuestionnaire}
                onApprove={() =>
                  handleApproveAndGenerate(request.listingId, request.id, request)
                }
                onDeny={() => handleDeny(request.listingId, request.id)}
                renderQuestionnaire={renderQuestionnaire}
              />
            );
          })}
        </Box>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            sx={{
              marginTop: "1rem",
              display: "flex",
              justifyContent: "center",
            }}
          />
        )}
      </>
    );
  };

  return (
    <Box sx={ViewingRequestsStyles.scheduledViewingsSection}>
      <Typography variant="h5" sx={ViewingRequestsStyles.sectionTitle}>
        Viewing Requests
      </Typography>
      <Tabs
        value={currentTab}
        onChange={(e, newValue) => setCurrentTab(newValue)}
      >
        {/* <Tab label="Pending" value="pending" /> */}
        <Tab label="Approved" value="approved" />
        <Tab label="Denied" value="denied" />
      </Tabs>
      <Box sx={{ marginTop: "1rem" }}>
        {loadingViewings ? (
          <Typography sx={ViewingRequestsStyles.message}>
            Loading viewings...
          </Typography>
        ) : (
          <>
            {/* Pending tab rendering - commented out (pending tab disabled) */}
            {/* {currentTab === "pending" &&
              (pendingRequests.length === 0 ? (
                <Typography sx={ViewingRequestsStyles.message}>
                  No pending requests.
                </Typography>
              ) : (
                renderRequests(pendingRequests, pendingPage, setPendingPage)
              ))} */}
            {currentTab === "approved" &&
              (approvedRequests.length === 0 ? (
                <Typography sx={ViewingRequestsStyles.message}>
                  No approved requests.
                </Typography>
              ) : (
                renderRequests(approvedRequests, approvedPage, setApprovedPage)
              ))}
            {currentTab === "denied" &&
              (deniedRequests.length === 0 ? (
                <Typography sx={ViewingRequestsStyles.message}>
                  No denied requests.
                </Typography>
              ) : (
                renderRequests(deniedRequests, deniedPage, setDeniedPage)
              ))}
          </>
        )}
      </Box>
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewingRequests;

